import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Zap, Ticket, Users, Calendar, FileText, KeyRound, MessageCircle, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'quick_actions',
  category: 'personal',
  titleKey: 'dashboard.widgets.quickActions.title',
  descriptionKey: 'dashboard.widgets.quickActionsDesc',
  defaultSize: 'md',
  icon: Zap,
};

// First "greenfield" widget — proves the registry pattern works for new
// additions, not just migrations. Pure navigation, no backend calls.
// Each action is filtered by the same module gating the sidebar uses, so a
// scoped agent only sees actions they can perform.
export default function QuickActionsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasModule, isAdmin } = useAuth();

  const actions = [
    { key: 'new_ticket', label: t('dashboard.widgets.quickActions.newTicket', 'New ticket'), icon: Ticket, to: '/tickets?compose=1', module: 'tickets' },
    { key: 'companies', label: t('dashboard.widgets.quickActions.companies', 'Companies'), icon: Users, to: '/companies', module: 'companies' },
    { key: 'calendar', label: t('dashboard.widgets.quickActions.calendar', 'Calendar'), icon: Calendar, to: '/calendar', module: 'calendar' },
    { key: 'kb', label: t('dashboard.widgets.quickActions.kb', 'Knowledge base'), icon: FileText, to: '/kb', module: 'knowledge_base' },
    { key: 'vault', label: t('dashboard.widgets.quickActions.vault', 'Vault'), icon: KeyRound, to: '/vault', module: 'vault' },
    { key: 'chat', label: t('dashboard.widgets.quickActions.chat', 'Team chat'), icon: MessageCircle, to: '/chat', module: 'chat' },
    { key: 'workshop', label: t('dashboard.widgets.quickActions.workshop', 'Workshop'), icon: Wrench, to: '/workshop', module: 'workshop' },
  ];

  const visible = actions.filter(a => isAdmin || hasModule(a.module));

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Zap size={16} />
          {t('dashboard.widgets.quickActions.title', 'Quick actions')}
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 8,
        padding: '8px 16px 16px',
      }}>
        {visible.map(action => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              onClick={() => navigate(action.to)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 6,
                padding: '14px 8px',
                background: 'var(--c-surface-2)',
                border: '1px solid var(--c-border)',
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--c-text)',
                fontSize: 11,
                fontWeight: 500,
                textAlign: 'center',
                transition: 'background 0.15s, transform 0.1s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--c-surface)';
                e.currentTarget.style.borderColor = 'var(--c-brand)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--c-surface-2)';
                e.currentTarget.style.borderColor = 'var(--c-border)';
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'rgba(59, 130, 246, 0.12)',
                color: 'var(--c-brand)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={18} />
              </div>
              <span>{action.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
